/**
 * Compliance-window status (spec §7). The app computes status LIVE for
 * display (the aging board never shows stale data); the daily Power
 * Automate flow persists the same transitions into
 * StoreAuditStatus.WindowStatus for notifications and analytics — both
 * sides implement the rules below, so keep them in sync with
 * docs/flows.md.
 */

export type WindowStatus = 'current' | 'due_soon' | 'overdue' | 'never';

export interface WindowInput {
  /** ISO date/time of the last submitted audit; null/'' = never audited. */
  lastCompleted: string | null;
  /** ISO date/time the next audit is due; null/'' = never audited. */
  nextDue: string | null;
  /** Template compliance window in days (e.g. 45). */
  frequencyDays: number;
  /** due_soon begins after this % of the window has elapsed (default 80). */
  dueSoonThresholdPct: number;
}

const DAY_MS = 86_400_000;

const EASTERN_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The Eastern calendar day of a moment, as a UTC-midnight timestamp (so two
 * of them subtract to whole days). A bare "YYYY-MM-DD" is that day as written.
 */
function easternDayMs(value: string | Date): number {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return Date.parse(`${value}T00:00:00Z`);
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return NaN;
  const parts = Object.fromEntries(EASTERN_DAY.formatToParts(d).map((p) => [p.type, p.value]));
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
}

/**
 * Whole EASTERN CALENDAR days from `from` until `to` (negative = past) —
 * owner ruling 2026-10-25 (round 5: an email said 14 days overdue, the board
 * 13). Flows 1 and 4 count the same way, so app and emails always agree: a
 * clock due on the 10th is due soon all of the 10th and overdue on the 11th.
 */
export function daysUntil(to: string, from: Date): number {
  return Math.round((easternDayMs(to) - easternDayMs(from)) / DAY_MS);
}

export function computeWindowStatus(
  input: WindowInput,
  today: Date = new Date(),
): WindowStatus {
  if (!input.nextDue) return 'never';
  const remaining = daysUntil(input.nextDue, today);
  if (remaining < 0) return 'overdue';
  // due_soon = the tail of the window: with 45 days and 80%, the last 9.
  const pct = input.dueSoonThresholdPct > 0 ? input.dueSoonThresholdPct : 80;
  const tailDays = dueSoonTailDays(input.frequencyDays, pct);
  return remaining <= tailDays ? 'due_soon' : 'current';
}

/**
 * The due-soon tail in whole days — INTEGER maths, exactly the flows'
 * `div(add(mul(freq, sub(100, pct)), 99), 100)` (Flow 1 TailDays, Flow 4
 * Tail_days). The old `ceil(freq × (1 − pct/100))` went through a float:
 * 30 × 0.3 = 9.000000000000002 → 10 days here, 9 in the flows (whole-app
 * review 2026-10-26); today's 45/15/30/90 at 80 % happened to agree.
 */
export function dueSoonTailDays(frequencyDays: number, pct: number): number {
  return Math.ceil((frequencyDays * (100 - pct)) / 100);
}

/** Days overdue (0 when not overdue) — drives sorting and email copy. */
export function daysOverdue(
  nextDue: string | null,
  today: Date = new Date(),
): number {
  if (!nextDue) return 0;
  return Math.max(0, -daysUntil(nextDue, today));
}

export const WINDOW_LABELS: Record<WindowStatus, string> = {
  current: 'Current',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  never: 'Never audited',
};

/** Sort weight: most urgent first on boards. */
export const WINDOW_URGENCY: Record<WindowStatus, number> = {
  overdue: 0,
  never: 1,
  due_soon: 2,
  current: 3,
};
